import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, 
  AlertTriangle, 
  CheckCircle, 
  AlertCircle,
  Clock,
  MapPin,
  User,
  Truck,
  Search,
  X,
  RefreshCw,
  Filter,
  Eye,
  ThumbsUp,
  ShieldCheck
} from 'lucide-react';
import { alertsAPI } from '../services/api';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({
    totalAlerts: 0,
    activeAlerts: 0,
    criticalAlerts: 0,
    resolvedAlerts: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'company_admin' || user.role === 'super_admin';

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await alertsAPI.getAll();
      
      if (response.success) {
        const alertsData = response.data || [];
        setAlerts(alertsData);
        
        // Calculate stats from alerts
        const activeAlerts = alertsData.filter(a => a.status === 'active').length;
        const criticalAlerts = alertsData.filter(a => 
          a.severity === 'critical' || a.severity === 'high'
        ).length;
        const resolvedAlerts = alertsData.filter(a => a.status === 'resolved').length;
        
        setStats({
          totalAlerts: alertsData.length,
          activeAlerts,
          criticalAlerts,
          resolvedAlerts
        });
      } else {
        setError(response.message || 'Failed to fetch alerts');
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError(err.message || 'Unable to load alerts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch stats separately
  const fetchStats = useCallback(async () => {
    try {
      const response = await alertsAPI.getStats();
      if (response.success) {
        setStats(response.data || {
          totalAlerts: 0,
          activeAlerts: 0,
          criticalAlerts: 0,
          resolvedAlerts: 0
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      // If stats endpoint fails, calculate from alerts
      const activeAlerts = alerts.filter(a => a.status === 'active').length;
      const criticalAlerts = alerts.filter(a => 
        a.severity === 'critical' || a.severity === 'high'
      ).length;
      const resolvedAlerts = alerts.filter(a => a.status === 'resolved').length;
      
      setStats({
        totalAlerts: alerts.length,
        activeAlerts,
        criticalAlerts,
        resolvedAlerts
      });
    }
  }, [alerts]);

  // Handle resolve alert
  const handleResolveAlert = async (alertId) => {
    try {
      setActionLoading(prev => ({ ...prev, [alertId]: true }));
      
      const response = await alertsAPI.markAsResolved(alertId);
      
      if (response.success) {
        // Update the alert in state
        setAlerts(prevAlerts => 
          prevAlerts.map(alert => 
            alert._id === alertId 
              ? { 
                  ...alert, 
                  status: 'resolved', 
                  resolvedAt: new Date().toISOString(),
                  resolvedBy: user
                } 
              : alert
          )
        );
        
        // Update stats
        setStats(prev => ({
          ...prev,
          resolvedAlerts: prev.resolvedAlerts + 1,
          activeAlerts: Math.max(0, prev.activeAlerts - 1)
        }));
        
        setSelectedAlert(null);
        setShowDetailModal(false);
      } else {
        alert(response.message || 'Failed to resolve alert');
      }
    } catch (err) {
      console.error('Error resolving alert:', err);
      alert(err.message || 'Failed to resolve alert. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [alertId]: false }));
    }
  };

  // Handle acknowledge alert
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      setActionLoading(prev => ({ ...prev, [alertId]: true }));
      
      const response = await alertsAPI.markAsAcknowledged(alertId);
      
      if (response.success) {
        // Update the alert in state
        setAlerts(prevAlerts => 
          prevAlerts.map(alert => 
            alert._id === alertId 
              ? { 
                  ...alert, 
                  acknowledged: true, 
                  acknowledgedAt: new Date().toISOString(),
                  acknowledgedBy: user
                } 
              : alert
          )
        );
        
        setSelectedAlert(null);
        setShowDetailModal(false);
      } else {
        alert(response.message || 'Failed to acknowledge alert');
      }
    } catch (err) {
      console.error('Error acknowledging alert:', err);
      alert(err.message || 'Failed to acknowledge alert. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [alertId]: false }));
    }
  };

  // Handle view details
  const handleViewDetails = (alert) => {
    setSelectedAlert(alert);
    setShowDetailModal(true);
  };

  // Filter alerts
  const filteredAlerts = alerts.filter(alert => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      (alert.title && alert.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (alert.message && alert.message.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (alert.driver?.driverId && alert.driver.driverId.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (alert.vehicle?.registration && alert.vehicle.registration.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Status filter
    switch (filter) {
      case 'active': return matchesSearch && alert.status === 'active';
      case 'critical': return matchesSearch && (alert.severity === 'critical' || alert.severity === 'high');
      case 'resolved': return matchesSearch && alert.status === 'resolved';
      case 'all': return matchesSearch;
      default: return matchesSearch;
    }
  });

  // Helper functions
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-orange-100 text-orange-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'acknowledged': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'fatigue': return <Clock className="w-5 h-5" />;
      case 'speed': return <AlertTriangle className="w-5 h-5" />;
      case 'maintenance': return <Truck className="w-5 h-5" />;
      case 'safety': return <AlertCircle className="w-5 h-5" />;
      case 'system': return <Bell className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'fatigue': return 'bg-purple-100 text-purple-800';
      case 'speed': return 'bg-red-100 text-red-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'safety': return 'bg-orange-100 text-orange-800';
      case 'system': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Initialize
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-8 h-8 text-[#e28743]" />
              Safety & Wellness Alerts
            </h1>
            <p className="text-gray-600 mt-1">
              Monitor and manage all fleet alerts in real-time
            </p>
          </div>
          
          <div className="flex items-center gap-3 mt-4 md:mt-0">
            <button
              onClick={fetchAlerts}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            {isAdmin && (
              <button
                className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] flex items-center gap-2"
                onClick={() => alert('Create alert feature coming soon!')}
              >
                <Bell className="w-4 h-4" />
                Create Alert
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { 
              label: 'Total Alerts', 
              value: stats.totalAlerts, 
              icon: Bell, 
              color: 'bg-[#e28743]',
              bgColor: 'bg-[#e28743] bg-opacity-10'
            },
            { 
              label: 'Active Alerts', 
              value: stats.activeAlerts, 
              icon: AlertTriangle, 
              color: 'bg-orange-600',
              bgColor: 'bg-orange-100'
            },
            { 
              label: 'Critical/High', 
              value: stats.criticalAlerts, 
              icon: AlertCircle, 
              color: 'bg-red-600',
              bgColor: 'bg-red-100'
            },
            { 
              label: 'Resolved', 
              value: stats.resolvedAlerts, 
              icon: CheckCircle, 
              color: 'bg-green-600',
              bgColor: 'bg-green-100'
            }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center">
                <div className={`p-2 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search alerts by title, message, driver, or vehicle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743]"
              />
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* Status Filters */}
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'All', icon: Bell, count: stats.totalAlerts },
              { id: 'active', label: 'Active', icon: AlertTriangle, count: stats.activeAlerts },
              { id: 'critical', label: 'Critical', icon: AlertCircle, count: stats.criticalAlerts },
              { id: 'resolved', label: 'Resolved', icon: CheckCircle, count: stats.resolvedAlerts }
            ].map((filterOption) => (
              <button
                key={filterOption.id}
                onClick={() => setFilter(filterOption.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  filter === filterOption.id 
                    ? 'bg-[#e28743] text-white' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <filterOption.icon className="w-4 h-4" />
                {filterOption.label} ({filterOption.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e28743]"></div>
          </div>
        ) : error ? (
          <div className="col-span-full bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Alerts</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchAlerts}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
            >
              Retry
            </button>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl shadow-sm p-8 text-center">
            <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Alerts Found</h3>
            <p className="text-gray-500">
              {searchTerm || filter !== 'all'
                ? 'No alerts match your filters. Try adjusting your search criteria.'
                : 'No alerts have been created yet.'}
            </p>
          </div>
        ) : (
          filteredAlerts.map((alert) => (
            <div key={alert._id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              {/* Alert Header */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(alert.type)}`}>
                      {alert.type?.charAt(0).toUpperCase() + alert.type?.slice(1) || 'Alert'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                      {alert.severity?.charAt(0).toUpperCase() + alert.severity?.slice(1) || 'Normal'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDate(alert.createdAt)}
                  </span>
                </div>
                
                <h3 className="font-semibold text-gray-900 text-lg mb-2">{alert.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-2">{alert.message}</p>
              </div>

              {/* Alert Details */}
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {alert.driver && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 truncate">
                        {alert.driver.name || alert.driver.driverId || 'Unknown Driver'}
                      </span>
                    </div>
                  )}
                  
                  {alert.vehicle && (
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 truncate">
                        {alert.vehicle.registration || 'Unknown Vehicle'}
                      </span>
                    </div>
                  )}
                  
                  {alert.location && (
                    <div className="col-span-2 flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {alert.location.address || 'Unknown Location'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewDetails(alert)}
                    className="flex-1 px-3 py-2 bg-[#304a3f] hover:bg-[#253830] text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Eye size={14} />
                    View Details
                  </button>
                  
                  {isAdmin && alert.status === 'active' && (
                    <>
                      <button
                        onClick={() => handleAcknowledgeAlert(alert._id)}
                        disabled={actionLoading[alert._id]}
                        className="px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {actionLoading[alert._id] ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        ) : (
                          <ThumbsUp size={14} />
                        )}
                        Ack
                      </button>
                      <button
                        onClick={() => handleResolveAlert(alert._id)}
                        disabled={actionLoading[alert._id]}
                        className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {actionLoading[alert._id] ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        ) : (
                          <ShieldCheck size={14} />
                        )}
                        Resolve
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Alert Detail Modal */}
      {showDetailModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(selectedAlert.type)}`}>
                      {selectedAlert.type?.charAt(0).toUpperCase() + selectedAlert.type?.slice(1) || 'Alert'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(selectedAlert.severity)}`}>
                      {selectedAlert.severity?.charAt(0).toUpperCase() + selectedAlert.severity?.slice(1) || 'Normal'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedAlert.status)}`}>
                      {selectedAlert.status?.charAt(0).toUpperCase() + selectedAlert.status?.slice(1) || 'Active'}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedAlert.title}</h2>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                    {selectedAlert.message}
                  </p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Created</h3>
                    <p className="text-gray-700">{formatFullDate(selectedAlert.createdAt)}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h3>
                    <p className="text-gray-700">{formatFullDate(selectedAlert.updatedAt)}</p>
                  </div>
                  
                  {selectedAlert.driver && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Driver</h3>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">
                          {selectedAlert.driver.name || selectedAlert.driver.driverId || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  )}

                  {selectedAlert.vehicle && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Vehicle</h3>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">
                          {selectedAlert.vehicle.registration || 'Unknown'}
                        </span>
                        {selectedAlert.vehicle.model && (
                          <span className="text-gray-500 text-sm">
                            ({selectedAlert.vehicle.model})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Location */}
                {selectedAlert.location && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Location</h3>
                    <div className="flex items-start gap-2 bg-gray-50 p-4 rounded-lg">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {selectedAlert.location.address || 'Unknown Location'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Acknowledged Info */}
                {selectedAlert.acknowledged && selectedAlert.acknowledgedBy && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <ThumbsUp className="w-4 h-4 text-blue-600" />
                      <h3 className="text-sm font-medium text-blue-800">Acknowledged</h3>
                    </div>
                    <p className="text-blue-700 text-sm">
                      By {selectedAlert.acknowledgedBy.name} at {formatFullDate(selectedAlert.acknowledgedAt)}
                    </p>
                  </div>
                )}

                {/* Resolved Info */}
                {selectedAlert.status === 'resolved' && selectedAlert.resolvedBy && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="w-4 h-4 text-green-600" />
                      <h3 className="text-sm font-medium text-green-800">Resolved</h3>
                    </div>
                    <p className="text-green-700 text-sm">
                      By {selectedAlert.resolvedBy.name} at {formatFullDate(selectedAlert.resolvedAt)}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                
                {isAdmin && selectedAlert.status === 'active' && (
                  <>
                    <button
                      onClick={() => handleAcknowledgeAlert(selectedAlert._id)}
                      disabled={actionLoading[selectedAlert._id]}
                      className="px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading[selectedAlert._id] ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <ThumbsUp className="w-4 h-4" />
                      )}
                      Acknowledge
                    </button>
                    <button
                      onClick={() => handleResolveAlert(selectedAlert._id)}
                      disabled={actionLoading[selectedAlert._id]}
                      className="px-4 py-3 bg-[#e28743] hover:bg-[#d47834] text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading[selectedAlert._id] ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <ShieldCheck className="w-4 h-4" />
                      )}
                      Resolve
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;